/**
 * The open task pocket: which task's details show, and where the pocket
 * sits — beside its task on a board, or docked at the right of the canvas
 * (spec §6.5). Mirrored across desktop windows, so a pocket opened from a
 * toast or the tray shows in the workspace window, including one that
 * mounts the app later.
 */
import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";

export type TaskPocketAnchor =
  | { kind: "board"; frameId: string }
  | { kind: "docked" };

/** The pocket's main page, or its More details page. */
export type TaskPocketPage = "main" | "details";

const isPage = (value: unknown): value is TaskPocketPage =>
  value === "main" || value === "details";

interface TaskPocketState {
  taskId: string | null;
  requestId: string | null;
  beginOpen: () => string;
  clearForProjectChange: () => void;
  anchor: TaskPocketAnchor | null;
  page: TaskPocketPage;
  /** Opens on the main page. */
  open: (taskId: string, anchor: TaskPocketAnchor) => void;
  setPage: (page: TaskPocketPage) => void;
  close: () => void;
}

export const useTaskPocketStore = create<TaskPocketState>()(
  mirroredStore("taskPocket", (set) => ({
    taskId: null,
    requestId: null,
    beginOpen: () => {
      const requestId = crypto.randomUUID();
      set({ requestId });
      return requestId;
    },
    clearForProjectChange: () =>
      set({ taskId: null, anchor: null, page: "main" }),
    anchor: null,
    page: "main",
    open: (taskId, anchor) =>
      set({ taskId, anchor, page: "main", requestId: crypto.randomUUID() }),
    setPage: (page) => set({ page }),
    close: () =>
      set({
        taskId: null,
        anchor: null,
        page: "main",
        requestId: crypto.randomUUID(),
      }),
  })),
);

export function hydrateTaskPocket(snapshot: unknown): void {
  if (!snapshot || typeof snapshot !== "object") return;
  const value = snapshot as {
    taskId?: unknown;
    anchor?: unknown;
    requestId?: unknown;
    page?: unknown;
  };
  const page = isPage(value.page) ? value.page : undefined;
  // A page change alone (`setPage`).
  if (
    page &&
    value.taskId === undefined &&
    value.anchor === undefined &&
    value.requestId === undefined
  ) {
    useTaskPocketStore.setState({ page });
    return;
  }
  const requestId =
    typeof value.requestId === "string" || value.requestId === null
      ? value.requestId
      : useTaskPocketStore.getState().requestId;
  if (
    value.taskId === undefined &&
    (typeof value.requestId === "string" || value.requestId === null)
  ) {
    useTaskPocketStore.setState({ requestId });
    return;
  }
  const anchor = value.anchor as Partial<TaskPocketAnchor> | null;
  if (value.taskId === null) {
    useTaskPocketStore.setState({
      taskId: null,
      anchor: null,
      page: "main",
      requestId,
    });
    return;
  }
  if (typeof value.taskId !== "string" || !anchor) return;
  if (
    anchor.kind === "docked" ||
    (anchor.kind === "board" && typeof anchor.frameId === "string")
  ) {
    useTaskPocketStore.setState({
      taskId: value.taskId,
      requestId,
      anchor: anchor as TaskPocketAnchor,
      page: page ?? "main",
    });
  }
}
